import Collapse from '../../../components/Collapse/Collapse';
import CountryLabel from '../../../components/CountryLabel/CountryLabel';
import { ReactComponent as PaymentIsntructionIcon } from '../../../icons/payment-instruction.svg';
import { ReactComponent as PaymentIsntructionPaidIcon } from '../../../icons/instruction-paid.svg';
import { ReactComponent as FolderIcon } from '../../../icons/folder-outline.svg';
import { ReactComponent as AgendaIcon } from '../../../icons/agenda-circle.svg';
import { ReactComponent as Arrow } from '../../../icons/select-chevron.svg';
import Tooltip from '../../../components/Tooltip/Tooltip';
import { formatDate, formatNumber } from '../../../core/helpers';
import classNames from 'classnames';
import { CompletedFilingItemData } from './SellerCompletedFiling';
import { useTranslation } from 'react-i18next';
import { normalizeDateFE } from '../../../core/normalization';
import {
  PaymentConfirmationFile,
  SellerFilingReportToConfirm,
} from '../../../core/types';
import Button from '../../../components/Button';
import { ReactComponent as AgendaSubmittedIcon } from '../../../icons/agenda-submitted.svg';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  data: CompletedFilingItemData;
  initialOpened?: boolean;
  onFolderBtnClick: (
    files: PaymentConfirmationFile[],
    date: Date,
    c: string
  ) => () => void;
  onAmountBtnClick: (r: SellerFilingReportToConfirm) => () => void;
  onClickAgenda: (r: SellerFilingReportToConfirm) => () => void;
}

export default function SellerCompletedFilingItem({
  data,
  initialOpened,
  onFolderBtnClick,
  onAmountBtnClick,
  onClickAgenda,
}: Props) {
  const { t } = useTranslation();

  const renderPeriod = (report: SellerFilingReportToConfirm) => {
    let formatStr = 'MMMM yyyy';

    if (report.period.type === 'year') {
      formatStr = 'yyyy';
    }

    if (report.period.type === 'quarter') {
      formatStr = 'QQQ yyyy';
    }

    if (
      report.country.short_name === 'IT' &&
      report.period.type !== 'quarter'
    ) {
      return t('Payment for *period*').replace(
        '*period*',
        formatDate(
          normalizeDateFE(report.period.from_date),
          report.period.type === 'month' ? 'MMM yyyy' : formatStr
        )
      );
    }

    return formatDate(normalizeDateFE(report.period.from_date), formatStr);
  };

  return (
    <Collapse
      initialOpened={initialOpened}
      className='SellerCompletedFiling__collapse mb-2'
      triggerRenderer={(isOpened, toggleCollapse) => (
        <div
          className='ps-3 pe-2 py-2 cursor-pointer d-flex align-items-start'
          onClick={toggleCollapse}
        >
          <div className='fw-800 flex-shrink-0 SellerCompletedFiling__top'>
            {formatDate(data.month, 'MMMM yyyy')}
          </div>
          <div className='d-flex align-items-center justify-content-between flex-grow-1'>
            <div>
              <div className='d-flex align-items-center'>
                <span className='fw-600'>{t('Total paid')}:</span>
                {Object.entries(data.amounts).map(([k, v], i) => (
                  <div key={k} className='SellerCompletedFiling__amount'>
                    &nbsp;{i > 0 ? ' ' : ''}
                    {formatNumber(v / 100, {
                      currency: k,
                    })}
                    &nbsp;
                  </div>
                ))}
              </div>
            </div>
            <Arrow
              className={classNames('SellerCompletedFiling__arrow', {
                'SellerCompletedFiling__arrow--active': isOpened,
              })}
            />
          </div>
        </div>
      )}
      contentRenderer={(ref) => (
        <div ref={ref}>
          <div className='d-grid SellerCompletedFiling__row px-3 py-2 mx-0 Text--gray'>
            <div>{t('Country')}</div>
            <div>{t('Period')}</div>
            <div>{t('Files')}</div>
            <div>{t('VAT paid')}</div>
          </div>
          {data.items.map((r) => (
            <div
              className='d-grid SellerCompletedFiling__row px-3 py-2 mx-0'
              key={uuidv4()}
            >
              <div>
                <CountryLabel
                  country={r.type === 'oss' ? 'EU' : r.country.short_name}
                />
              </div>
              <div>{renderPeriod(r)}</div>
              <div>
                <Button
                  styling='naked'
                  className='d-flex align-items-center SellerCompletedFiling__btn'
                  onClick={
                    r.confirmation?.documents
                      ? onFolderBtnClick(
                          r.confirmation?.documents || [],
                          normalizeDateFE(
                            r.payment_instruction?.due_date || r.deadline_date
                          ),
                          r.confirmation.comment
                        )
                      : undefined
                  }
                  disabled={!r.confirmation?.documents.length}
                >
                  <FolderIcon className='SellerCompletedFiling__icon me-1' />
                  {r.confirmation?.documents.length ? t('Open') : t('No files')}
                </Button>
              </div>
              <div className='d-flex justify-content-between'>
                {r.payment_instruction ? (
                  <Tooltip
                    delay={500}
                    content={
                      r.payment_instruction.confirmed
                        ? t('Payment confirmed')
                        : t('Payment not confirmed')
                    }
                  >
                    <Button
                      styling='naked'
                      className='d-flex align-items-center SellerCompletedFiling__btn'
                      onClick={onAmountBtnClick(r)}
                    >
                      {r.payment_instruction?.confirmed ? (
                        <PaymentIsntructionPaidIcon className='SellerCompletedFiling__icon me-1' />
                      ) : (
                        <PaymentIsntructionIcon className='SellerCompletedFiling__icon me-1' />
                      )}
                      {formatNumber(r.payment_instruction.amount.amount / 100, {
                        currency: r.payment_instruction.amount.currency,
                      })}
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    styling='naked'
                    className='d-flex align-items-center SellerCompletedFiling__btn'
                    disabled={!r.payment_instruction}
                  >
                    <PaymentIsntructionIcon className='SellerCompletedFiling__icon me-1' />
                    {t('No instruction')}
                  </Button>
                )}
                {r.country.short_name === 'DE' &&
                  r.type === 'local' &&
                  !!r.payment_instruction && (
                    <div className='ms-3'>
                      <Button
                        styling='naked'
                        className='Modal__btn'
                        onClick={onClickAgenda(r)}
                      >
                        {r.agenda_confirmed ? (
                          <AgendaSubmittedIcon />
                        ) : (
                          <AgendaIcon />
                        )}
                      </Button>
                    </div>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}
    />
  );
}
